<template>
  <InfuseModal :title="t('title', { id: chargingStationType.type })" @close="emit('close')">
    <form id="edit-charging-station-type" @submit.prevent="onSave()">
      <div class="flex max-h-[475px] max-w-[965px] flex-col gap-4 overflow-y-auto">
        <div class="flex flex-col items-center gap-4 lg:flex-row">
          <InfuseField :label="t('type.label')" class="basis-1/2" size="small">
            <InfuseInput
              v-model="chargingStationType.type"
              class="w-full"
              size="small"
              :placeholder="t('type.placeholder')"
              autofocus
              required
            />
          </InfuseField>
          <InfuseField :label="t('manufacturer.label')" class="basis-1/2" size="small">
            <InfuseSearchSelect
              :modelValue="chargingStationType.manufacturer.name"
              :options="
                manufacturers.map((manufacturer: Manufacturer) => {
                  return { key: manufacturer.name, value: manufacturer.name };
                })
              "
              size="small"
              :placeholder="t('manufacturer.placeholder')"
              name="manufacturer"
              required
              @update:modelValue="onManufacturerUpdate"
            />
          </InfuseField>
        </div>

        <div class="flex items-center gap-4">
          <InfuseField
            :label="t('charging_station_type.fields.commercialType.label')"
            class="w-full"
            size="small"
            :optional="true"
          >
            <InfuseInput
              v-model="chargingStationType.commercialType"
              class="w-full"
              size="small"
              :placeholder="t('charging_station_type.fields.commercialType.placeholder')"
            />
          </InfuseField>
        </div>

        <div class="flex w-full flex-wrap gap-4">
          <EvseConnector
            v-for="(c, i) in chargingStationType.connectors"
            :key="`connector-${c.evseIdentifier}`"
            v-model="chargingStationType.connectors[i]"
            class="w-[467px]"
            :evseId="c.evseIdentifier"
            @remove="removeConnector($event)"
          />
        </div>
      </div>

      <InfuseButton class="mt-8 w-full" size="small" color="neutral" @click="addConnector">
        {{ t('addConnector') }}
        <font-awesome-icon icon="plus" class="ml-2" />
      </InfuseButton>
    </form>

    <template #actions>
      <InfuseButton
        data-testid="modal-confirm-button"
        type="submit"
        form="edit-charging-station-type"
        :disabled="loading"
        :loading="loading"
      >
        {{ t('save') }}
      </InfuseButton>
    </template>
  </InfuseModal>
</template>

<script setup lang="ts">
import { ChargingStationType, Evse, Manufacturer } from '@infuse/cpms/config/store';
import { InfuseButton, InfuseField, InfuseInput, InfuseModal, InfuseSearchSelect } from '@infuse/ui/components';
import { ref, unref } from 'vue';
import { useI18n } from 'vue-i18n';

import EvseConnector from './EvseConnector.vue';
import { FlatConnector, flatConnectorDefaults } from './model';

interface Props {
  value: ChargingStationType;
  manufacturers: Manufacturer[];
  loading: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  manufacturers: () => [],
  loading: false
});

interface Emits {
  (e: 'searchManufacturers', query: string): void;
  (e: 'close'): void;
  (e: 'save', item: ChargingStationType): void;
}

const emit = defineEmits<Emits>();

const { t } = useI18n();

const chargingStationType = ref<{
  id?: number;
  type: string;
  commercialType: string | undefined;
  manufacturer: { id?: number; name: string };
  connectors: FlatConnector[];
}>({
  id: props.value.id,
  type: props.value.type,
  commercialType: props.value.commercialType || undefined,
  manufacturer: {
    id: props.value.manufacturer.id,
    name: props.value.manufacturer.name
  },
  connectors: props.value.evses?.map((evse: Evse) => {
    if (evse.connectors) {
      return {
        ...flatConnectorDefaults,
        evseCapabilities: evse.capabilities,
        ...evse.connectors[0],
        evseIdentifier: evse.identifier
      };
    } else {
      return { ...flatConnectorDefaults, evseCapabilities: evse.capabilities, evseIdentifier: evse.identifier };
    }
  }) || [{ ...flatConnectorDefaults, evseCapabilities: [] }]
});

function addConnector() {
  const nextIdentifier = Math.max(0, ...chargingStationType.value.connectors.map((o) => o.evseIdentifier)) + 1;
  chargingStationType.value.connectors.push({
    ...flatConnectorDefaults,
    evseIdentifier: nextIdentifier,
    evseCapabilities: []
  });
}

function removeConnector(evseIdentifier: number) {
  const idx = chargingStationType.value.connectors.findIndex((c) => c.evseIdentifier === evseIdentifier);
  chargingStationType.value.connectors.splice(idx, 1);
}

function onManufacturerUpdate(manufacturerName: string) {
  const manufacturer = props.manufacturers.find((manufacturer: Manufacturer) => manufacturer.name === manufacturerName);

  if (manufacturer) {
    chargingStationType.value.manufacturer = manufacturer;
  } else {
    chargingStationType.value.manufacturer.id = undefined;
    chargingStationType.value.manufacturer.name = manufacturerName;
  }
}

function onSave() {
  const csType = unref(chargingStationType);

  const evses: Evse[] = csType.connectors.map((connector) => {
    return {
      identifier: connector.evseIdentifier,
      capabilities:
        connector.evseCapabilities && connector.evseCapabilities.length === 0 ? undefined : connector.evseCapabilities,
      connectors: [
        {
          connectorType: connector.connectorType,
          current: connector.current,
          maxAmperage: connector.maxAmperage,
          maxElectricPower: connector.maxElectricPower,
          phases: connector.phases,
          physicalReference: connector.physicalReference,
          voltage: connector.voltage
        }
      ]
    };
  });

  const formattedChargingStationType: ChargingStationType = {
    id: csType.id,
    type: csType.type,
    commercialType: csType.commercialType,
    manufacturer: {
      id: csType.manufacturer.id,
      name: csType.manufacturer.name
    },
    evses: evses
  };

  emit('save', formattedChargingStationType);
}
</script>

<i18n>
{
  "en": {
    "title": "Update {id}",
    "type": {
      "placeholder": "Enter a type",
      "label": "Type"
    },
    "manufacturer": {
      "placeholder": "Enter a manufacturer",
      "label": "Manufacturer"
    },
    "addConnector": "Add connector",
    "save": "Save"
  },
  "nl": {
    "title": "Update {id}",
    "type": {
      "placeholder": "Voer een type in",
      "label": "Type"
    },
    "manufacturer": {
      "placeholder": "Voer een fabrikant in",
      "label": "Fabrikant"
    },
    "addConnector": "Aansluiting toevoegen",
    "save": "Opslaan"
  }
}
</i18n>
